import React, { useState, useEffect, useCallback } from "react";
import "../css/others/Popup.css";
import PopUpBuildFormulaquerygenerater from "../others/PopUpBuildFormulaquerygenerater";
import { RuleGroupType } from "react-querybuilder";
import { apiCall } from "../main/ApiCall";
import ToastPopup from "./ToastPopup";
import { useNavigate } from "react-router-dom";
import { SecondConfirmationProps, BuildFormulaAttribute, DropdownOption, createToastMessage, ToastMessage } from "../interFaces/interfaces";
const BuildFormulaPopup: React.FC<SecondConfirmationProps> = ({ onConfirm, onClose, clientId }) => {
  const [query, setQuery] = useState<RuleGroupType>({ combinator: "and", rules: [] });
  const [attributes, setAttributes] = useState<BuildFormulaAttribute[]>([]);
  const [existingFilters, setExistingFilters] = useState<any[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>([]);
  const [questionText, setQuestionText] = useState<Record<string, string>>({});
  const [dateDropdownValues, setDateDropdownValues] = useState<number[]>([15, 30, 45]);
  const navigate = useNavigate();
  const [auditFrequency, setAuditFrequency] = useState<string>("30");
  const [meetingDuration, setMeetingDuration] = useState<string>("30");
  const [extensionDays, setExtensionDays] = useState<string>("3");
  const [campaignQcReviewDuration, setCampaignQcReviewDuration] = useState<string>("3");
  const [existingAuditConfig, setExistingAuditConfig] = useState<boolean>(false);
  const email = localStorage.getItem("email");
  const handleResetFilters = () => { setQuery({ combinator: "and", rules: [] }) };
  const getDropdownValues = (category: string): number[] => {
    const option = dropdownOptions.find((opt) => opt.category === category);
    return option ? option.values : [];
  };
  const getDropdownQuestion = (category: string): string => {
    return questionText[category] || "";
  };
  const renderDropdownOptions = (values: number[], unit: string) => {
    return values.map((value) => (
      <option key={value} value={value}>{value} {unit}</option>
    ));
  };
  const getUnitForCategory = (category: string): string => {
    switch (category) {
      case "audit_frequency_options":
        return "Days";
      case "audit_meeting_duration":
        return "Min";
      case "campaign_review_by_qc":
        return "Days";
      case "report_finalization_after_campaign":
        return "Days";
      default:
        return "";
    }
  };
  const validateAuditQuestions = (): boolean => {
    const auditCategories = [{ category: "audit_frequency_options", value: auditFrequency, label: "Audit Frequency" }, { category: "audit_meeting_duration", value: meetingDuration, label: "Audit Meeting Duration" }, { category: "campaign_review_by_qc", value: campaignQcReviewDuration, label: "Campaign Review by QC" }, { category: "report_finalization_after_campaign", value: extensionDays, label: "Report Finalization After Campaign" }];
    const unansweredQuestions = auditCategories.filter(item => {
      const question = getDropdownQuestion(item.category);
      return question && !item.value;
    });
    if (unansweredQuestions.length > 0) {
      const questionList = unansweredQuestions
        .map(item => getDropdownQuestion(item.category))
        .join(", ");
      setMessage(createToastMessage(`Please answer the following questions before saving: ${questionList}`, "warning", 3000));
      return false;
    }
    return true;
  };
  const fetchFilters = useCallback(async () => {
    try {
      const response = await apiCall("get", `/api/client-mappings/default-filters?clientId=${clientId}`);
      const filters = response.filters || [];
      setExistingFilters(filters);
      const transformedFilters = filters.map((filter: any) => ({ field: filter.field, operator: filter.operator, value: filter.value }));
      setQuery({ combinator: "and", rules: transformedFilters });
    } catch (error) {
      console.error("Failed to fetch default filters:", error);
    }
  }, [clientId]);
  const fetchAttributes = useCallback(async () => {
    try {
      const [response, , configResponse, dropdownOptionsRes, normalDateRes] =
        await Promise.all([
          apiCall("get", `/api/client-apis?clientId=${clientId}`),
          apiCall("get", `/api/client-mappings/default-filters?clientId=${clientId}`),
          apiCall("get", `/api/client-config/${clientId}`),
          apiCall("get", `/api/build-formula/dropdown-config`),
          apiCall("get", `/api/dropdown-config/normal`)
        ]);
      setDropdownOptions(dropdownOptionsRes);
      if (normalDateRes && normalDateRes.values && normalDateRes.values.length > 0) {
        setDateDropdownValues(normalDateRes.values);
      }
      if (dropdownOptionsRes && Array.isArray(dropdownOptionsRes)) {
        const newQuestionText: Record<string, string> = {};
        dropdownOptionsRes.forEach((option: DropdownOption) => {
          if (option.question && option.category) {
            newQuestionText[option.category] = option.question;
          }
        });
        setQuestionText(newQuestionText);
      }
      const fieldsObject = response.attributeToLabelMapping;
      const mandatoryFields = ["Team Size", "Project Category", "Billing Type", "Target ML", "Project Start Date", "Project End Date"];
      const mappedAttributes = Object.entries(fieldsObject)
        .filter(([key]) => mandatoryFields.includes(key))
        .map(([key, label]) => ({ name: label as string, label: key, type: key.toLowerCase().includes("date") ? "date" : "string", }));
      setAttributes(mappedAttributes);
      if (configResponse) {
        setAuditFrequency(configResponse.projectPickUpIntervel?.toString() || "30");
        setMeetingDuration(configResponse.meetingDuration?.toString() || "30");
        setCampaignQcReviewDuration(configResponse.campaignQcReviewDuration?.toString() || "3");
        setExtensionDays(configResponse.extensionDays?.toString() || "3");
        setExistingAuditConfig(true);
      }
    } catch (error) {
      console.error("Failed to fetch attributes:", error);
    }
  }, [clientId]);
  useEffect(() => { fetchFilters(); fetchAttributes() }, [fetchFilters, fetchAttributes]);
  const handleCancelOrClose = async () => {
    try {
      await apiCall("patch", `/api/client-mappings/default-filters`, { clientId, isDisabled: true, });
      const auditRequestBody = { clientId, projectPickUpIntervel: parseInt(auditFrequency), meetingDuration: parseInt(meetingDuration), campaignQcReviewDuration: parseInt(campaignQcReviewDuration), extensionDays: parseInt(extensionDays), };
      if (existingAuditConfig) {
        await apiCall("put", "/api/client-config", auditRequestBody);
      } else {
        await apiCall("post", "/api/client-config", auditRequestBody);
      }
      onConfirm(false);
      onClose();
    } catch (error) {
      setMessage(createToastMessage("Error disabling the filters.", "error", 3000));
    }
  };
  const handleShowResults = async () => {
    if (!validateAuditQuestions()) {
      return;
    }
    if (query.rules.length === 0) {
      setMessage(createToastMessage("Please add filters before proceeding.", "warning", 3000));
      return;
    }
    const transformFieldName = (fieldName: string): string =>
      fieldName
        .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) =>
          index === 0 ? match.toLowerCase() : match.toUpperCase()
        )
        .replace(/\s+/g, "");
    const validRules = query.rules.filter(
      (rule) =>
        "field" in rule &&
        "operator" in rule &&
        rule.value !== undefined &&
        rule.value !== null &&
        rule.value !== "" &&
        !(rule.field === "Project Category" && rule.value === "0")
    );
    const queryParams = validRules.map((rule) => {
      if ("field" in rule && "operator" in rule && "value" in rule) {
        const transformedField = transformFieldName(rule.field);
        let value = rule.value;
        return { field: transformedField, operator: rule.operator, value };
      }
      return null;
    }).filter(Boolean);
    if (queryParams.length === 0) {
      setMessage(createToastMessage("Please add filters with valid values before proceeding.", "warning", 3000));
      return;
    }
    const requestBody = { clientId, filters: queryParams, isDisabled: true, updatedBy: email };
    const isUpdate = existingFilters.length > 0;
    try {
      const auditRequestBody = { clientId, projectPickUpIntervel: parseInt(auditFrequency), meetingDuration: parseInt(meetingDuration), campaignQcReviewDuration: parseInt(campaignQcReviewDuration), extensionDays: parseInt(extensionDays), updatedBy: email };
      if (isUpdate) {
        await apiCall("patch", `/api/client-mappings/default-filters`, requestBody);
      } else {
        await apiCall("post", `/api/client-mappings/default-filters`, requestBody);
      }
      if (existingAuditConfig) {
        await apiCall("put", "/api/client-config", auditRequestBody);
      } else {
        await apiCall("post", "/api/client-config", auditRequestBody);
      }
      setMessage(createToastMessage("Formula updated successfully", "success", 3000));
      setTimeout(() => { navigate("/createcampaign") }, 3000);
      setIsSubmitted(true);
    } catch (error) {
      setMessage(createToastMessage("Error applying filters or audit configuration", "error", 3000));
    }
  };
  const renderAuditQuestion = (category: string, value: string, onChange: (value: string) => void) => {
    const question = getDropdownQuestion(category);
    const unit = getUnitForCategory(category);
    const values = getDropdownValues(category);
    if (!question) return null;
    return (
      <div className="filter-fields1">
        <div className="display-screen-question">
          <p className="filter-header-p">{question}</p>
          <select className="audit-frequency-dropdown" value={value} onChange={(e) => onChange(e.target.value)} disabled={isSubmitted}>
            <option value="">Select</option>
            {renderDropdownOptions(values, unit)}
          </select>
        </div>
      </div>
    );
  };
  return (
    <div className="buildformula-overlay">
      <div className="buildformula-content">
        <div className="buildformula-header">
          <h3>Build Formula</h3>
          <button className="buildformula-close-button" onClick={handleCancelOrClose}>&times;</button>
        </div>
        <div className="filter-fields-box">
          <p className="filter-header-p">Select your filters</p>
          <button className="button-reset" onClick={handleResetFilters} disabled={isSubmitted}>Reset Filters</button>
        </div>
        <PopUpBuildFormulaquerygenerater attributes={attributes} onQueryChange={setQuery} query={query} disabled={isSubmitted} dateDropdownValues={dateDropdownValues} />
        {renderAuditQuestion("audit_frequency_options", auditFrequency, setAuditFrequency)}
        {renderAuditQuestion("audit_meeting_duration", meetingDuration, setMeetingDuration)}
        {renderAuditQuestion("campaign_review_by_qc", campaignQcReviewDuration, setCampaignQcReviewDuration)}
        {renderAuditQuestion("report_finalization_after_campaign", extensionDays, setExtensionDays)}
        <div className="button-container">
          <button className="cancelbutton" onClick={handleCancelOrClose}>Cancel</button>
          <button className="savebutton" onClick={handleShowResults} disabled={isSubmitted}> Confirm</button>
        </div>
      </div>
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default BuildFormulaPopup;